import {
  Component,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Optional,
  Inject,
  input,
  output,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { AgentChatToolCallsView } from "./ai-chat-tool-calls-view";
import type { Message } from "@ag-ui/core";
import {
  type AssistantMessage,
  type AgentChatAssistantMessageOnThumbsUpProps,
  type AgentChatAssistantMessageOnThumbsDownProps,
  type AgentChatAssistantMessageOnReadAloudProps,
  type AgentChatAssistantMessageOnRegenerateProps,
} from "./ai-chat-assistant-message.types";
import { AgentChatAssistantMessageRenderer } from "./ai-chat-assistant-message-renderer";
import {
  AgentChatAssistantMessageCopyButton,
  AgentChatAssistantMessageThumbsUpButton,
  AgentChatAssistantMessageThumbsDownButton,
} from "./ai-chat-assistant-message-buttons";
import { AgentChatAssistantMessageToolbar } from "./ai-chat-assistant-message-toolbar";
import { cn } from "../utils/utils";
import { AgentChatViewHandlers } from "./ai-chat-view-handlers";
import { ACHAT_VIEW_CONFIG, type AchatViewConfig } from "../core/achat-view-config";

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message",
  host: { "data-agent-ui": "" },
  imports: [
    CommonModule,
    AgentChatAssistantMessageRenderer,
    AgentChatAssistantMessageCopyButton,
    AgentChatAssistantMessageToolbar,
    AgentChatToolCallsView,
    AgentChatAssistantMessageThumbsUpButton,
    AgentChatAssistantMessageThumbsDownButton,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [class]="computedClass()" [attr.data-message-id]="message().id">
      <!-- Markdown Renderer -->
      <agent-chat-assistant-message-renderer [content]="message().content || ''" [inputClass]="markdownRendererClass()">
      </agent-chat-assistant-message-renderer>

      <!-- Tool Calls View -->
      @if (message().toolCalls && message()!.toolCalls!.length > 0) {
      <agent-chat-tool-calls-view [message]="message()!" [messages]="messages()" [isLoading]="isLoading()">
      </agent-chat-tool-calls-view>
      }

      <!-- Toolbar: show only when there is assistant text content -->
      @if (toolbarVisible() && hasMessageContent()) {
      <div agentChatAssistantMessageToolbar [inputClass]="toolbarClass()">
        <div class="achat-msg__actions">
          <agent-chat-assistant-message-copy-button
            [content]="message().content"
            [inputClass]="copyButtonClass()"
            (clicked)="handleCopy()"
          >
          </agent-chat-assistant-message-copy-button>
          @if (feedbackButtonsVisible() && handlers.hasAssistantThumbsUpHandler()) {
          <agent-chat-assistant-message-thumbs-up-button
            [inputClass]="thumbsUpButtonClass()"
            (clicked)="handleThumbsUp()"
          >
          </agent-chat-assistant-message-thumbs-up-button>
          } @if (feedbackButtonsVisible() && handlers.hasAssistantThumbsDownHandler()) {
          <agent-chat-assistant-message-thumbs-down-button
            [inputClass]="thumbsDownButtonClass()"
            (clicked)="handleThumbsDown()"
          >
          </agent-chat-assistant-message-thumbs-down-button>
          } @if (additionalToolbarItems()) {
          <ng-container [ngTemplateOutlet]="additionalToolbarItems() || null"></ng-container>
          }
        </div>
      </div>
      }
    </div>
  `,
})
export class AgentChatAssistantMessage {
  // Class inputs for styling default components
  readonly markdownRendererClass = input<string | undefined>(undefined);

  readonly toolbarClass = input<string | undefined>(undefined);

  readonly copyButtonClass = input<string | undefined>(undefined);

  readonly thumbsUpButtonClass = input<string | undefined>(undefined);

  readonly thumbsDownButtonClass = input<string | undefined>(undefined);

  readonly readAloudButtonClass = input<string | undefined>(undefined);

  readonly regenerateButtonClass = input<string | undefined>(undefined);

  readonly toolCallsViewClass = input<string | undefined>(undefined);

  // Regular inputs
  readonly message = input.required<AssistantMessage>();

  readonly messages = input<Message[]>([]);

  readonly isLoading = input<boolean>(false);

  readonly additionalToolbarItems = input<any | undefined>(undefined);

  readonly toolbarVisible = input<boolean>(true);

  readonly inputClass = input<string | undefined>(undefined);

  handlers: AgentChatViewHandlers;

  private readonly viewConfig = inject(ACHAT_VIEW_CONFIG, { optional: true }) as AchatViewConfig | null;

  protected readonly feedbackButtonsVisible = computed(() => this.viewConfig?.showFeedbackButtons !== false);

  constructor(
    @Optional()
    @Inject(AgentChatViewHandlers)
    handlers?: AgentChatViewHandlers | null,
  ) {
    this.handlers = handlers || new AgentChatViewHandlers();
  }

  // Output events
  thumbsUp = output<AgentChatAssistantMessageOnThumbsUpProps>();

  thumbsDown = output<AgentChatAssistantMessageOnThumbsDownProps>();

  readAloud = output<AgentChatAssistantMessageOnReadAloudProps>();

  regenerate = output<AgentChatAssistantMessageOnRegenerateProps>();

  customClass = signal<string | undefined>(undefined);

  computedClass = computed(() => cn("achat-assistant-msg prose max-w-full break-words", this.customClass()));

  hasMessageContent(): boolean {
    const raw = (this.message()?.content ?? "") as any;
    const content = typeof raw === "string" ? raw : String(raw ?? "");
    return content.trim().length > 0;
  }

  handleCopy(): void {}

  handleThumbsUp(): void {
    this.thumbsUp.emit({ message: this.message() });
  }

  handleThumbsDown(): void {
    this.thumbsDown.emit({ message: this.message() });
  }

  handleReadAloud(): void {
    this.readAloud.emit({ message: this.message() });
  }

  handleRegenerate(): void {
    this.regenerate.emit({ message: this.message() });
  }
}
